import { useState } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, Mail, RotateCcw, Check, AtSign, Loader2 } from "lucide-react";
import type { Property, Expense } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { MailDocumentReviewDialog } from "@/components/mail-document-review-dialog";

interface MailDocRow {
  id: number;
  agentmailAttachmentName: string;
  senderEmail: string | null;
  senderName: string | null;
  receivedAt: string | null;
  mimeType: string;
  status: string;
  geminiDocumentType: string | null;
  extractedData: Record<string, any> | null;
  suggestedPropertyId: number | null;
}

const DOC_TYPE_BADGE: Record<string, { label: string; className: string }> = {
  invoice: { label: "Invoice", className: "bg-blue-100 text-blue-700" },
  expense: { label: "Expense", className: "bg-green-100 text-green-700" },
  bid: { label: "Bid", className: "bg-purple-100 text-purple-700" },
  contract: { label: "Contract", className: "bg-orange-100 text-orange-700" },
  other: { label: "Other", className: "bg-gray-100 text-gray-600" },
};

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  pending_review: { label: "Pending", className: "bg-yellow-100 text-yellow-700" },
  approved: { label: "Approved", className: "bg-green-100 text-green-700" },
  dismissed: { label: "Dismissed", className: "bg-gray-100 text-gray-500" },
};

export function ForwardedDocsTab({ properties }: { properties: Property[] }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilterState] = useState<string>(
    () => localStorage.getItem("pp:financials-docs-status-filter") ?? "pending_review",
  );
  const setStatusFilter = (next: string) => {
    localStorage.setItem("pp:financials-docs-status-filter", next);
    setStatusFilterState(next);
  };
  const [reviewDoc, setReviewDoc] = useState<MailDocRow | null>(null);

  const configQuery = useQuery<{ configured: boolean; inboxAddress: string }>({
    queryKey: ["/api/app/mail-documents/config-status"],
  });

  const docsQuery = useQuery<MailDocRow[]>({
    queryKey: ["/api/app/mail-documents", statusFilter],
    queryFn: async () => {
      const res = await fetch(`/api/app/mail-documents?status=${encodeURIComponent(statusFilter)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load documents");
      return res.json();
    },
  });

  const pollMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => apiRequest("POST", "/api/app/mail-documents/poll", {}),
    onSuccess: (data: any) => {
      toast({ title: `Poll complete: ${data.processed} new, ${data.skipped} skipped, ${data.errors} errors` });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mail-documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/mail-documents/pending-count"] });
    },
    onError: (err: any) => {
      toast({ title: "Poll failed", description: parseErrorMessage(err, "Unknown error"), variant: "destructive" });
    },
  });

  const docs = docsQuery.data ?? [];
  const inboxAddress = configQuery.data?.inboxAddress ?? "padpulse-3042@agentmail.to";
  const isConfigured = configQuery.data?.configured ?? false;

  return (
    <div className="space-y-4" data-testid="tab-mail-inbox">
      {/* Header / setup card */}
      <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3" data-tour="forwarded-docs-heading">
        <AtSign className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-blue-900 dark:text-blue-200">Forward documents to your PadPulse inbox</p>
          <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
            Forward any email with invoice, receipt, bid, or contract attachments to{" "}
            <code className="font-mono bg-blue-100 dark:bg-blue-900/50 px-1 rounded select-all" data-tour="forwarded-docs-address">{inboxAddress}</code>.
            AI will classify and extract data automatically — then review here before committing.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-blue-300 text-blue-700 hover:bg-blue-100"
          onClick={() => pollMutation.mutate()}
          disabled={pollMutation.isPending || !isConfigured}
          data-testid="button-poll-mail"
          data-tour="forwarded-docs-check-now"
        >
          {pollMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}
          Check Now
        </Button>
      </div>

      {!isConfigured && !configQuery.isLoading && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm text-amber-700 dark:text-amber-300">
          AgentMail is not configured on this server. Set <code className="font-mono bg-amber-100 dark:bg-amber-900/50 px-1 rounded">AGENTMAIL_API_KEY</code> to enable automatic email polling.
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="select-mail-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending_review">Pending Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-500">{docs.length} document{docs.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Document list */}
      <div data-tour="forwarded-docs-list">
      {docsQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-lg" />)}
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-2">
          <Mail className="h-10 w-10" />
          <p className="text-sm">
            {statusFilter === "pending_review" ? "No documents pending review." : `No ${statusFilter.replace("_", " ")} documents.`}
          </p>
          {statusFilter === "pending_review" && (
            <p className="text-xs text-gray-400">Forward an email with attachments to {inboxAddress} to get started.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {docs.map((doc) => {
            const typeInfo = DOC_TYPE_BADGE[doc.geminiDocumentType ?? "other"] ?? DOC_TYPE_BADGE.other;
            const statusInfo = STATUS_BADGE[doc.status] ?? { label: doc.status, className: "bg-gray-100 text-gray-500" };
            const amount = doc.extractedData?.totalAmount;
            return (
              <div
                key={doc.id}
                className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-50 cursor-pointer"
                onClick={() => doc.status === "pending_review" ? setReviewDoc(doc) : undefined}
                data-testid={`row-mail-doc-${doc.id}`}
              >
                <FileText className="h-5 w-5 text-gray-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{doc.agentmailAttachmentName}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {doc.senderName || doc.senderEmail || "Unknown sender"}
                    {doc.receivedAt ? ` · ${new Date(doc.receivedAt).toLocaleDateString()}` : ""}
                    {doc.extractedData?.vendorName ? ` · ${doc.extractedData.vendorName}` : ""}
                    {doc.suggestedPropertyId != null && (() => {
                      const p = properties.find((pr) => pr.id === doc.suggestedPropertyId);
                      return p ? <span className="ml-1 text-[11px] bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 rounded px-1">{p.name}</span> : null;
                    })()}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {amount != null && (
                    <span className="text-sm font-medium text-gray-700">
                      ${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${typeInfo.className}`}>
                    {typeInfo.label}
                  </span>
                  <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${statusInfo.className}`}>
                    {statusInfo.label}
                  </span>
                  {doc.status === "pending_review" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`button-review-${doc.id}`}>
                      Review
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>

      {reviewDoc && (
        <MailDocumentReviewDialog
          doc={reviewDoc}
          properties={properties}
          open={true}
          onOpenChange={(o) => { if (!o) setReviewDoc(null); }}
          onDone={() => { setReviewDoc(null); docsQuery.refetch(); }}
        />
      )}
    </div>
  );
}

// ─── Statements Tab (Task #1906 / Task #1917) ─────────────────────────────────

